import {
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { GameTemplate, GameTemplateWithStatus } from '@/lib-backend';
import { GameTemplatesService } from '@/app/services/game-templates.service';
import { AuthService } from '@/app/services/auth.service';
import { gameModeLabel, winModeLabel } from '@/app/utils/game';
import { resolveGameContentUrl } from '@/app/utils/game-content-url';
import { TemplateEditDialog } from './template-edit-dialog/template-edit-dialog';

@Component({
    selector: 'app-game-templates',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        IconField,
        InputIcon,
        InputText,
        Message,
        TableModule,
        Tag,
        Tooltip,
        TemplateEditDialog,
    ],
    templateUrl: './game-templates.html',
})
export class GameTemplates implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    protected readonly templatesService = inject(GameTemplatesService);

    /** Templates are platform-wide; only global admins may create them. */
    protected readonly canManage = computed(
        () => this.authService.currentAdmin()?.Access._type === 'global',
    );

    protected readonly dialogVisible = signal(false);
    protected readonly editingTemplate = signal<GameTemplate | null>(null);

    protected readonly searchTerm = signal('');
    protected readonly filteredTemplates = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        if (!term) return this.templatesService.templates();
        return this.templatesService.templates().filter((item) => {
            const t = item.Template;
            const title = t.Title.toLowerCase();
            const desc = t.Description.toLowerCase();
            const kind = (t.GameKind?.Name ?? t.GameKindId).toLowerCase();
            return (
                title.includes(term) ||
                desc.includes(term) ||
                kind.includes(term)
            );
        });
    });

    protected readonly gameModeLabel = gameModeLabel;
    protected readonly winModeLabel = winModeLabel;
    protected readonly resolveGameContentUrl = resolveGameContentUrl;

    ngOnInit(): void {
        this.templatesService.loadTemplates();

        this.route.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((params) => {
                if (params.get('action') === 'add' && this.canManage()) {
                    this.openCreate();
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { action: null },
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }
            });
    }

    protected refresh(): void {
        this.templatesService.loadTemplates();
    }

    protected openCreate(): void {
        this.editingTemplate.set(null);
        this.dialogVisible.set(true);
    }

    protected openEdit(item: GameTemplateWithStatus): void {
        this.editingTemplate.set(item.Template);
        this.dialogVisible.set(true);
    }

    protected trackById(_index: number, item: GameTemplateWithStatus): string {
        return item.Template.Id;
    }
}
